import React, { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import {
  IoTimeOutline,
  IoPeopleOutline,
  IoBus,
  IoSchool,
  IoBusOutline,
} from "react-icons/io5";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { ref, onValue, off } from "firebase/database";

import { activeRoute } from "../api/activeRoute";
import WatchBusSkeleton from "../components/skeleton-loading/WatchBusSkeleton";
import { db } from "../services/firebase";

const formatName = (name) => {
  if (!name || typeof name !== "string" || name.trim() === "") return "-";
  return name
    .trim()
    .toLocaleLowerCase("tr-TR")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toLocaleUpperCase("tr-TR") + word.slice(1))
    .join(" ");
};

const formatTime = (timeStr) => (timeStr ? timeStr.slice(0, 5) : "");

const BusIcon = L.divIcon({
  className: "",
  html: renderToString(
    <div className="flex items-center justify-center bg-[#00306b] w-10 h-10 rounded-full border-2 border-white shadow-lg text-white text-xl">
      <IoBus />
    </div>,
  ),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -24],
});

const SchoolIcon = L.divIcon({
  className: "",
  html: renderToString(
    <div className="flex items-center justify-center bg-indigo-900 w-10 h-10 rounded-full border-2 border-white shadow-lg text-white text-xl">
      <IoSchool />
    </div>,
  ),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -24],
});

// İlk yüklemede otobüs ve okulu kapsayacak şekilde haritayı odaklayan kontrolcü
function MapController({ busLocation, schoolLocation, hasLoadedLocations }) {
  const map = useMap();
  const hasFocusedOnceRef = useRef(false);

  useEffect(() => {
    if (
      !hasLoadedLocations ||
      !busLocation ||
      !schoolLocation ||
      hasFocusedOnceRef.current
    )
      return;

    const bounds = L.latLngBounds([
      [busLocation.latitude, busLocation.longitude],
      [schoolLocation.latitude, schoolLocation.longitude],
    ]);

    map.fitBounds(bounds, {
      padding: [80, 80],
      maxZoom: 15,
      animate: true,
    });

    hasFocusedOnceRef.current = true;
  }, [busLocation, schoolLocation, hasLoadedLocations, map]);

  return null;
}

export default function WatchBus() {
  const [isLoading, setIsLoading] = useState(true);
  const [routeData, setRouteData] = useState(null);
  const [hasLoadedLocations, setHasLoadedLocations] = useState(false);

  const [busLocation, setBusLocation] = useState({
    latitude: 41.009,
    longitude: 28.977,
  });
  const [schoolLocation, setSchoolLocation] = useState({
    latitude: 41.009,
    longitude: 28.977,
  });

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await activeRoute.getByToken();
        const responseData = response?.data;

        if (responseData && responseData.status && responseData.data) {
          const rawData = responseData.data;

          if (rawData.car?.lat && rawData.car?.lng) {
            setBusLocation({
              latitude: parseFloat(rawData.car.lat),
              longitude: parseFloat(rawData.car.lng),
            });
          }

          if (rawData.school?.lat && rawData.school?.lng) {
            setSchoolLocation({
              latitude: parseFloat(rawData.school.lat),
              longitude: parseFloat(rawData.school.lng),
            });
          }

          setHasLoadedLocations(true);

          // Return (dönüş) rotasında durak sırasını ters çevirme
          if (
            rawData.active_type === "return" &&
            Array.isArray(rawData.stops)
          ) {
            rawData.stops = [...rawData.stops].reverse();
          }

          setRouteData(rawData);
        } else {
          setRouteData(null);
        }
      } catch (error) {
        console.error("Konum yüklenirken hata oluştu:", error);
        setRouteData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadLocations();
  }, []);

  // Firebase Realtime Database Dinleyicisi
  useEffect(() => {
    if (!routeData?.car?.id) return;

    const carRef = ref(db, `cars/${routeData.car.id}`);

    const unsubscribe = onValue(
      carRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data && data.lat && data.lng) {
          setBusLocation({
            latitude: parseFloat(data.lat),
            longitude: parseFloat(data.lng),
          });
        }
      },
      (error) => {
        console.error("Firebase okuma hatası:", error);
      },
    );

    return () => off(carRef, "value", unsubscribe);
  }, [routeData]);

  if (isLoading) {
    return <WatchBusSkeleton />;
  }

  // Sefer Bilgileri
  const driverName = formatName(routeData?.driver?.name);
  const hostessName = formatName(routeData?.hostess?.name);
  const plate = routeData?.car?.plaka_no || "-";
  const seatCapacity = routeData?.car?.capacity
    ? `${routeData.car.capacity} Kişilik`
    : "-";

  let serviceTime = "-";
  if (routeData) {
    const isActiveReturn = routeData.active_type === "return";
    const startTime = formatTime(
      isActiveReturn
        ? routeData.return_start_time
        : routeData.departure_start_time,
    );
    const endTime = formatTime(
      isActiveReturn ? routeData.return_end_time : routeData.departure_end_time,
    );

    if (startTime && endTime) {
      serviceTime = `${startTime} - ${endTime}`;
    }
  }

  const schoolName = routeData?.school?.name || "-";
  const stops = routeData?.stops || [];

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#f8f9ff] text-[#0b1c30] antialiased flex flex-col md:flex-row">
      {routeData ? (
        <>
          {/* Harita Alanı */}
          <div className="w-full md:grow h-[45vh] md:h-[calc(100vh-80px)] z-0 relative">
            <MapContainer
              center={[busLocation.latitude, busLocation.longitude]}
              zoom={14}
              className="w-full h-full"
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Otobüs Marker */}
              <Marker
                position={[busLocation.latitude, busLocation.longitude]}
                icon={BusIcon}
              >
                <Popup>
                  <div className="text-center font-semibold">
                    <p className="text-[#00306b] !m-0 font-bold">{plate}</p>
                    <p className="text-xs text-gray-500 !m-0">{driverName}</p>
                  </div>
                </Popup>
              </Marker>

              {/* Okul Marker */}
              <Marker
                position={[schoolLocation.latitude, schoolLocation.longitude]}
                icon={SchoolIcon}
              >
                <Popup>
                  <div className="text-center font-semibold">
                    <p className="text-indigo-950 font-bold !m-0">
                      {schoolName}
                    </p>
                    <p className="text-xs text-gray-500 !m-0">Varış Noktası</p>
                  </div>
                </Popup>
              </Marker>

              {/* Haritayı iki konuma göre otomatik sığdıran kontrol */}
              <MapController
                busLocation={busLocation}
                schoolLocation={schoolLocation}
                hasLoadedLocations={hasLoadedLocations}
              />
            </MapContainer>

            {/* Mobil Bilgi Bilgi Etiketi */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-4 py-2 rounded-xl shadow-md border border-gray-100 md:hidden z-10">
              <p className="text-xs text-gray-500 font-medium">Servis Saati</p>
              <p className="text-sm font-bold text-[#00306b]">{serviceTime}</p>
            </div>
          </div>

          {/* Sağ Panel */}
          <div className="w-full md:w-[420px] md:min-w-[420px] bg-white border-t md:border-t-0 md:border-l border-gray-100 shadow-xl flex flex-col h-full md:h-[calc(100vh-80px)] overflow-y-auto z-20">
            {/* Canlı Takip Başlık */}
            <div className="p-5 border-b border-gray-50 bg-[#f8f9ff]/50">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-2 py-1 rounded-md">
                    Canlı Takip
                  </span>
                  <h2 className="text-xl font-bold text-[#00306b] mt-2">
                    Servis Durumu
                  </h2>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-400 font-medium block">
                    SERVİS SAATİ
                  </span>
                  <span className="text-sm font-bold text-[#0b1c30]">
                    {serviceTime}
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg text-[#00306b]">
                  <IoTimeOutline className="text-2xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Aktif Hat</p>
                  <p className="text-sm font-bold text-[#00306b] flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-600"></span>
                    {routeData?.route_name || "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Sürücü ve Hostes */}
            <div className="p-5 border-b border-gray-50 grid grid-cols-2 gap-4 bg-white">
              <div className="bg-[#f8f9ff] p-3 rounded-xl border border-gray-50">
                <span className="text-[11px] text-gray-400 uppercase font-semibold tracking-wider block mb-0.5">
                  Sürücü
                </span>
                <p className="text-sm font-bold text-gray-800">{driverName}</p>
                {routeData?.driver?.phone && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {routeData.driver.phone}
                  </p>
                )}
              </div>
              <div className="bg-[#f8f9ff] p-3 rounded-xl border border-gray-50">
                <span className="text-[11px] text-gray-400 uppercase font-semibold tracking-wider block mb-0.5">
                  Hostes
                </span>
                <p className="text-sm font-bold text-gray-800">{hostessName}</p>
                {routeData?.hostess?.phone && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {routeData.hostess.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Araç Bilgisi */}
            <div className="p-5 border-b border-gray-50 bg-white">
              <div className="flex items-center justify-between p-3.5 bg-blue-50/40 rounded-xl border border-blue-50">
                <div className="flex items-center gap-3">
                  <div className="bg-[#00306b] text-white px-2.5 py-1.5 rounded-md font-bold text-sm tracking-wide shadow-sm whitespace-nowrap">
                    {plate}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-700 leading-tight">
                      {[routeData?.car?.brand_name, routeData?.car?.model_name]
                        .filter(Boolean)
                        .join(" ") || "-"}
                    </p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                      Sürücü: {driverName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-gray-500 text-xs font-semibold bg-white px-2.5 py-1 rounded-full border border-gray-100">
                  <IoPeopleOutline className="text-sm" />
                  {routeData?.car?.capacity
                    ? `${routeData.car.capacity} Kişilik`
                    : "-"}
                </div>
              </div>
            </div>

            {/* Durak / Rota Listesi */}
            <div className="p-5 grow bg-white">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-5">
                Durak / Rota Bilgisi
              </h3>
              <div className="relative pl-6 space-y-6 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-100">
                {stops.length > 0 ? (
                  stops.map((stop, index) => {
                    const isActive = Number(stop.activeStop) === 1;

                    return (
                      <div
                        key={stop.id || index}
                        className="relative flex flex-col items-start"
                      >
                        {isActive ? (
                          <span className="absolute -left-[24px] top-1 w-4 h-4 rounded-full border-2 border-blue-600 bg-white z-10 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                          </span>
                        ) : (
                          <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-gray-300 z-10" />
                        )}

                        <div className="w-full pl-0.5">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wide block mb-0.5 ${
                              isActive ? "text-blue-600" : "text-gray-400"
                            }`}
                          >
                            Durak
                          </span>
                          <p
                            className={`text-base font-bold leading-tight ${
                              isActive ? "text-blue-900" : "text-gray-700"
                            }`}
                          >
                            {formatName(stop.street_name)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-400">
                    Kayıtlı durak bulunamadı.
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Servis Aktif Değil Ekranı */
        <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-50 min-h-[calc(100vh-80px)] w-full">
          <IoBusOutline className="text-6xl text-gray-400 mb-4" />
          <h2 className="text-lg font-bold text-gray-700 text-center">
            Servis saati aktif değil
          </h2>
          <p className="mt-2 text-base font-semibold text-gray-500 text-center max-w-md">
            Canlı konum takibi sadece okul giriş ve çıkış saatlerinde aktiftir.
            Yaklaşan sefer saatinde tekrar kontrol ediniz.
          </p>
        </div>
      )}
    </div>
  );
}
